import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FiDownload, FiFilter, FiX, FiChevronDown, FiSearch } from "react-icons/fi";
import Header from "../component/Header";
import { API_BASE_URL } from "../utils/apiConfig";

function Sales() {
    const [stats, setStats] = useState({
        totalOrdersToday: 0,
        avgOrderValue: "0.00",
        totalRevenueToday: 0
    });
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterFlavor, setFilterFlavor] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState("");
    const [deviceSearchQuery, setDeviceSearchQuery] = useState("");
    const [isDeviceDropdownOpen, setIsDeviceDropdownOpen] = useState(false);
    const deviceDropdownRef = useRef(null);
    const [flavorSearchQuery, setFlavorSearchQuery] = useState("");
    const [isFlavorDropdownOpen, setIsFlavorDropdownOpen] = useState(false);
    const flavorDropdownRef = useRef(null);
    const userRole = localStorage.getItem("role");

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (deviceDropdownRef.current && !deviceDropdownRef.current.contains(event.target)) {
                setIsDeviceDropdownOpen(false);
            }
            if (flavorDropdownRef.current && !flavorDropdownRef.current.contains(event.target)) {
                setIsFlavorDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchData = useCallback(async (deviceId) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const deviceParam = deviceId ? `?device_id=${deviceId}` : "";

            const [statsRes, ordersRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/orders/stats${deviceParam}`, config),
                axios.get(`${API_BASE_URL}/api/orders${deviceParam}`, config)
            ]);
            setStats(statsRes.data);
            setOrders(ordersRes.data);
        } catch (err) {
            console.error("Failed to fetch sales data", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            try {
                const token = localStorage.getItem("token");
                const config = { headers: { Authorization: `Bearer ${token}` } };
                const devicesRes = await axios.get(`${API_BASE_URL}/api/devices`, config);
                setDevices(devicesRes.data);
                // auto-select first device for admin, all for superadmin
                let initialId = "";
                if (userRole !== "superadmin") {
                    initialId = devicesRes.data?.[0]?.device_id_PK || "";
                }
                setSelectedDeviceId(initialId);
                fetchData(initialId);
            } catch (err) {
                console.error("Failed to init sales data", err);
                fetchData("");
            }
        };
        init();
    }, [fetchData]);

    const handleDeviceChange = (e) => {
        const id = e.target.value;
        setSelectedDeviceId(id);
        setFilterFlavor("");
        fetchData(id);
    };

    const handleExportCSV = () => {
        if (filteredOrders.length === 0) return alert("No data to export");
        const headers = ["Order ID", "Transaction ID", "Machine", "Flavours", "Base", "Size", "Status", "Amount (INR)", "Time"];
        const csvRows = filteredOrders.map(order => [
            order.order_id_PK,
            `"${order.transaction_id || order.qr_id || 'N/A'}"`,
            `"${order.device_name || 'N/A'}"`,
            `"${order.flavor_name || 'N/A'}"`,
            `"${order.base_name || 'N/A'}"`,
            `"${order.shake_size || 'N/A'}"`,
            order.status,
            order.total_price,
            `"${new Date(order.created_at).toLocaleString()}"`
        ]);
        const csvContent = [headers, ...csvRows].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `sales_report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const uniqueFlavors = [...new Set(orders.map(o => o.flavor_name))].filter(Boolean);
    const filteredOrders = orders.filter(order => {
        // Exclude orders that don't have any flavours
        if (!order.flavor_name || order.flavor_name === "null" || order.flavor_name.trim() === "") {
            return false;
        }

        const matchesFlavor = filterFlavor === "" || order.flavor_name === filterFlavor;
        const query = searchQuery.toLowerCase();
        const matchesSearch = query === "" ||
            (order.order_id_PK?.toString().toLowerCase().includes(query)) ||
            (order.transaction_id?.toString().toLowerCase().includes(query)) ||
            (order.qr_id?.toString().toLowerCase().includes(query)) ||
            (order.device_name?.toLowerCase().includes(query)) ||
            (order.flavor_name?.toLowerCase().includes(query)) ||
            (order.base_name?.toLowerCase().includes(query));
        return matchesFlavor && matchesSearch;
    });

    if (loading) return <div className="bg-black min-h-screen text-white flex items-center justify-center font-bold">Loading Sales Data...</div>;

    return (
        <div className="bg-black min-h-screen text-white p-6">
            <Header />

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <p className="text-gray-400 text-sm">Total Orders Today</p>
                    <h2 className="text-green-400 text-3xl font-bold mt-2">{stats.totalOrdersToday}</h2>
                    <p className="text-green-500 text-xs mt-1">Live from machine fleet</p>
                </div>
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <p className="text-gray-400 text-sm">Average Order Value</p>
                    <h2 className="text-green-400 text-3xl font-bold mt-2">₹{stats.avgOrderValue}</h2>
                    <p className="text-gray-500 text-xs mt-1">Calculated today</p>
                </div>
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <p className="text-gray-400 text-sm">Total Revenue</p>
                    <h2 className="text-green-500 text-3xl font-bold mt-2">₹{stats.totalRevenueToday}</h2>
                    <p className="text-green-500 text-xs mt-1">Daily aggregated revenue</p>
                </div>
            </div>

            {/* Table Section */}
            <div className="grid grid-cols-1 gap-6 mt-8">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg">
                    <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                        <div>
                            <h2 className="text-xl font-semibold text-green-400">Transaction History</h2>
                            <p className="text-xs text-gray-500 mt-1">Displaying {filteredOrders.length} records</p>
                        </div>

                        <div className="flex items-center gap-3 flex-wrap">
                            {/* Search bar */}
                            <div className="relative flex items-center h-9">
                                <FiSearch size={13} className="absolute left-3 text-gray-500" />
                                <input
                                    type="text"
                                    placeholder="Search orders..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="h-full bg-black/40 border border-white/5 text-gray-300 text-[11px] font-bold rounded-xl pl-8 pr-8 focus:outline-none focus:ring-1 focus:ring-green-500 transition w-36 md:w-48 placeholder:text-gray-600"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        className="absolute right-3 text-gray-500 hover:text-red-400 transition"
                                    >
                                        <FiX size={13} />
                                    </button>
                                )}
                            </div>

                            {/* ── Device Selector ── */}
                            {devices.length > 0 && (
                                <div className="relative flex items-center h-9 bg-black/40 rounded-xl border border-white/5 transition focus-within:ring-1 focus-within:ring-green-500" ref={deviceDropdownRef}>
                                    <div
                                        className="w-full h-full flex items-center justify-between px-3 cursor-pointer"
                                        onClick={() => setIsDeviceDropdownOpen(!isDeviceDropdownOpen)}
                                    >
                                        <span className="text-gray-300 text-[11px] font-bold truncate pr-2 w-24 md:w-32">
                                            {selectedDeviceId ? (() => {
                                                const dev = devices.find(d => String(d.device_id_PK) === String(selectedDeviceId));
                                                return dev ? `${dev.device_name}${dev.user_location ? ` (${dev.user_location})` : (dev.location ? ` (${dev.location})` : '')}` : "All Devices";
                                            })() : "All Devices"}
                                        </span>
                                        <FiChevronDown size={11} className={`text-gray-600 transition-transform ${isDeviceDropdownOpen ? 'rotate-180' : ''}`} />
                                    </div>

                                    {isDeviceDropdownOpen && (
                                        <div className="absolute top-[calc(100%+8px)] left-0 z-50 w-[240px] bg-[#111211] border border-gray-800 rounded-xl shadow-2xl overflow-hidden">
                                            <div className="p-3 border-b border-gray-800">
                                                <div className="relative">
                                                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={12} />
                                                    <input
                                                        type="text"
                                                        placeholder="Search Device..."
                                                        value={deviceSearchQuery}
                                                        onChange={(e) => setDeviceSearchQuery(e.target.value)}
                                                        className="w-full bg-black border border-gray-800 text-gray-200 rounded-lg pl-8 pr-3 py-2 text-[11px] focus:outline-none focus:border-green-500/50 transition-colors placeholder:text-gray-600"
                                                        autoFocus
                                                    />
                                                </div>
                                            </div>
                                            <div className="max-h-64 overflow-y-auto custom-scrollbar text-[11px] font-medium p-1">
                                                <div
                                                    className={`px-3 py-2 mx-1 mb-1 rounded-lg cursor-pointer transition-colors ${!selectedDeviceId ? 'bg-green-500/10 text-green-400 font-bold' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                                                    onClick={() => {
                                                        handleDeviceChange({ target: { value: "" } });
                                                        setIsDeviceDropdownOpen(false);
                                                        setDeviceSearchQuery("");
                                                    }}
                                                >
                                                    All Devices
                                                </div>
                                                {(() => {
                                                    const filtered = devices.filter(d => 
                                                        (d.device_name || "").toLowerCase().includes(deviceSearchQuery.toLowerCase()) ||
                                                        (d.user_location || "").toLowerCase().includes(deviceSearchQuery.toLowerCase()) ||
                                                        (d.location || "").toLowerCase().includes(deviceSearchQuery.toLowerCase())
                                                    );
                                                    if (filtered.length === 0) return <div className="px-3 py-4 text-gray-500 text-center font-normal">No devices found</div>;
                                                    return filtered.map(device => (
                                                        <div
                                                            key={device.device_id_PK}
                                                            className={`px-3 py-2 mx-1 mb-1 rounded-lg cursor-pointer transition-colors truncate ${String(selectedDeviceId) === String(device.device_id_PK) ? 'bg-green-500/10 text-green-400 font-bold' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                                                            onClick={() => {
                                                                handleDeviceChange({ target: { value: device.device_id_PK } });
                                                                setIsDeviceDropdownOpen(false);
                                                                setDeviceSearchQuery("");
                                                            }}
                                                            title={`${device.device_name}${device.user_location ? ` (${device.user_location})` : (device.location ? ` (${device.location})` : '')}`}
                                                        >
                                                            {device.device_name} {(device.user_location || device.location) && <span className="text-gray-400 text-xs">({device.user_location || device.location})</span>}
                                                        </div>
                                                    ));
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Flavour filter */}
                            <div className="relative flex items-center h-9 bg-black/40 rounded-xl border border-white/5 transition focus-within:ring-1 focus-within:ring-green-500" ref={flavorDropdownRef}>
                                <div
                                    className="w-full h-full flex items-center justify-between px-3 cursor-pointer"
                                    onClick={() => setIsFlavorDropdownOpen(!isFlavorDropdownOpen)}
                                >
                                    <div className="flex items-center truncate">
                                        <FiFilter size={13} className="text-green-500/50 mr-2 shrink-0" />
                                        <span className="text-gray-300 text-[11px] font-bold truncate pr-3 w-32 md:w-36">
                                            {filterFlavor || "All Flavours"}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {filterFlavor && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setFilterFlavor("");
                                                }}
                                                className="text-gray-500 hover:text-red-400 transition"
                                            >
                                                <FiX size={13} />
                                            </button>
                                        )}
                                        <FiChevronDown size={11} className={`text-gray-600 transition-transform ${isFlavorDropdownOpen ? 'rotate-180' : ''}`} />
                                    </div>
                                </div>

                                {isFlavorDropdownOpen && (
                                    <div className="absolute top-[calc(100%+8px)] right-0 z-50 w-[240px] md:w-[280px] bg-[#111211] border border-gray-800 rounded-xl shadow-2xl overflow-hidden">
                                        <div className="p-3 border-b border-gray-800">
                                            <div className="relative">
                                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={12} />
                                                <input
                                                    type="text"
                                                    placeholder="Search Flavour..."
                                                    value={flavorSearchQuery}
                                                    onChange={(e) => setFlavorSearchQuery(e.target.value)}
                                                    className="w-full bg-black border border-gray-800 text-gray-200 rounded-lg pl-8 pr-3 py-2 text-[11px] focus:outline-none focus:border-green-500/50 transition-colors placeholder:text-gray-600"
                                                    autoFocus
                                                />
                                            </div>
                                        </div>
                                        <div className="max-h-64 overflow-y-auto custom-scrollbar text-[11px] font-medium p-1">
                                            <div
                                                className={`px-3 py-2 mx-1 mb-1 rounded-lg cursor-pointer transition-colors ${!filterFlavor ? 'bg-green-500/10 text-green-400 font-bold' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                                                onClick={() => {
                                                    setFilterFlavor("");
                                                    setIsFlavorDropdownOpen(false);
                                                    setFlavorSearchQuery("");
                                                }}
                                            >
                                                All Flavours
                                            </div>
                                            {(() => {
                                                const filteredFlavors = uniqueFlavors.filter(f => (f || "").toLowerCase().includes(flavorSearchQuery.toLowerCase()));
                                                if (filteredFlavors.length === 0) return <div className="px-3 py-4 text-gray-500 text-center font-normal">No flavours found</div>;
                                                return filteredFlavors.map(f => (
                                                    <div
                                                        key={f}
                                                        className={`px-3 py-2 mx-1 mb-1 rounded-lg cursor-pointer transition-colors truncate ${filterFlavor === f ? 'bg-green-500/10 text-green-400 font-bold' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                                                        onClick={() => {
                                                            setFilterFlavor(f);
                                                            setIsFlavorDropdownOpen(false);
                                                            setFlavorSearchQuery("");
                                                        }}
                                                        title={f}
                                                    >
                                                        {f}
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={handleExportCSV}
                                className="flex items-center gap-2 px-4 h-9 text-[11px] font-black uppercase tracking-widest border border-green-500/40 rounded-xl text-green-400 hover:bg-green-500 hover:text-black transition active:scale-95 shrink-0"
                            >
                                <FiDownload size={13} /> Export
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-gray-300">
                            <thead>
                                <tr className="border-b border-gray-700 text-gray-400">
                                    <th className="px-4 py-3 text-left">Order ID</th>
                                    <th className="px-4 py-3 text-left">Transaction ID</th>
                                    <th className="px-4 py-3 text-left">Machine</th>
                                    <th className="px-4 py-3 text-left">Flavours</th>
                                    <th className="px-4 py-3 text-left">Base</th>
                                    <th className="px-4 py-3 text-left">Size</th>
                                    <th className="px-4 py-3 text-left">Status</th>
                                    <th className="px-4 py-3 text-left">Amount</th>
                                    <th className="px-4 py-3 text-left">Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOrders.map((order, index) => (
                                    <tr key={order.order_id_PK} className="border-b border-gray-800 hover:bg-[#1e1e1e] transition">
                                        <td className="px-4 py-3 font-medium text-white">{order.order_id_PK || index + 1}</td>
                                        <td className="px-4 py-3 text-green-300 font-mono text-xs">
                                            {order.transaction_id || order.qr_id || "—"}
                                        </td>
                                        <td className="px-4 py-3 text-gray-400">{order.device_name}</td>
                                        <td className="px-4 py-3 text-white">{order.flavor_name}</td>
                                        <td className="px-4 py-3 text-white">{order.base_name}</td>
                                        <td className="px-4 py-3 text-white">{order.shake_size}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded text-[10px] uppercase font-bold ${order.status === 'paid' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
                                                {order.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-green-400 font-semibold">₹{order.total_price}</td>
                                        <td className="px-4 py-3 text-gray-500">{new Date(order.created_at).toLocaleString()}</td>
                                    </tr>
                                ))}
                                {filteredOrders.length === 0 && (
                                    <tr>
                                        <td colSpan="9" className="px-4 py-8 text-center text-gray-500 italic">No orders found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Sales;
